// src/components/admin-tools/ToolAgentsModal.tsx
//
// Modal "ver agentes" — wireframe §13 (dlgToolAgents): DUAS listas lado a lado
// (COM acesso × SEM acesso); clicar num agente abre CONFIRMAÇÃO (dlgTAConfirm)
// antes de adicionar/remover (PATCH /v1/admin/agent-tools, feito pelo caller).
// Mantém da versão anterior (PRs #182-184): modal larga, busca, paginação dos
// agentes com acesso, badges de tipo/escopo e o aviso de tool Core.

import { useMemo, useState } from 'react';
import type { AdminAgentTool, AgentListItem } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import {
  Bot,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Cog,
  Cpu,
  Globe,
  Loader2,
  Plus,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ToolRow } from './tools-helpers';

type BadgeVariant = 'default' | 'info' | 'brand' | 'warning' | 'success';

// Tipo do agente (archetype) — badge + ícone pra reconhecer de bate-pronto.
const ARCHETYPE_META: Record<string, { label: string; variant: BadgeVariant; icon: typeof Bot }> = {
  companion: { label: 'Companion', variant: 'brand', icon: Bot },
  domain_worker: { label: 'Worker', variant: 'info', icon: Cog },
  system: { label: 'Sistema', variant: 'default', icon: Cpu },
};
const SCOPE_META: Record<string, { label: string; variant: BadgeVariant; icon: typeof Globe }> = {
  global: { label: 'Global', variant: 'success', icon: Globe },
  tenant: { label: 'Tenant', variant: 'warning', icon: Building2 },
};

function archetypeMeta(a?: string | null) {
  return ARCHETYPE_META[a ?? ''] ?? { label: a ?? '—', variant: 'default' as BadgeVariant, icon: Bot };
}
function scopeMeta(s?: string | null) {
  return SCOPE_META[s ?? ''] ?? { label: s ?? '—', variant: 'default' as BadgeVariant, icon: Globe };
}

const AGENTS_PER_PAGE = 10;

interface PendingAction {
  ref: string; // slug (ou UUID quando slug ausente) — o que o PATCH aceita
  name: string;
  action: 'add' | 'remove';
}

export function ToolAgentsModal({
  tool,
  onClose,
  agents,
  assigned,
  loadingAgents,
  savingAgent,
  onToggleAgent,
}: {
  tool: ToolRow | null;
  onClose: () => void;
  /** Todos os agentes (deduped) — base da coluna "Sem acesso". */
  agents: AgentListItem[];
  /** agent_tools enabled da tool selecionada. */
  assigned: AdminAgentTool[];
  loadingAgents: boolean;
  /** ref do agente sendo salvo (slug/uuid) ou null. */
  savingAgent: string | null;
  /** Executa o PATCH (true=dar acesso, false=remover) + recarrega listas. */
  onToggleAgent: (ref: string, enabled: boolean) => Promise<void>;
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pending, setPending] = useState<PendingAction | null>(null);

  // Reset de busca/página/confirm ao trocar de tool — ajuste DURANTE o render
  // (padrão React "adjusting state when props change"; evita setState em effect).
  const [prevSlug, setPrevSlug] = useState<string | undefined>(tool?.slug);
  if (tool?.slug !== prevSlug) {
    setPrevSlug(tool?.slug);
    setSearch('');
    setPage(0);
    setPending(null);
  }

  const agentBySlug = useMemo(() => {
    const m = new Map<string, AgentListItem>();
    for (const a of agents) m.set(a.slug, a);
    return m;
  }, [agents]);

  const q = search.trim().toLowerCase();

  // COM acesso — ordenada por slug, filtrada pela busca, paginada (10/página).
  const withAccess = useMemo(() => {
    return assigned
      .slice()
      .sort((a, b) => (a.agent_slug ?? '').localeCompare(b.agent_slug ?? ''))
      .filter(
        (at) =>
          !q ||
          (at.agent_slug ?? '').toLowerCase().includes(q) ||
          (at.agent_name ?? '').toLowerCase().includes(q),
      );
  }, [assigned, q]);

  // Busca muda → volta pra primeira página (mesmo padrão de ajuste no render).
  const [prevQ, setPrevQ] = useState(q);
  if (q !== prevQ) {
    setPrevQ(q);
    setPage(0);
  }

  // SEM acesso — companions primeiro, depois workers/sistema.
  const withoutAccess = useMemo(() => {
    const has = new Set(assigned.map((at) => at.agent_slug));
    const order = (x: AgentListItem) =>
      x.archetype === 'companion' ? 0 : x.archetype === 'domain_worker' ? 1 : 2;
    return agents
      .filter((a) => !has.has(a.slug))
      .filter(
        (a) => !q || a.slug.toLowerCase().includes(q) || (a.name ?? '').toLowerCase().includes(q),
      )
      .sort((a, b) => order(a) - order(b) || a.slug.localeCompare(b.slug));
  }, [agents, assigned, q]);

  const pageCount = Math.max(1, Math.ceil(withAccess.length / AGENTS_PER_PAGE));
  const pageItems = withAccess.slice(page * AGENTS_PER_PAGE, (page + 1) * AGENTS_PER_PAGE);

  const confirming = pending ? savingAgent === pending.ref : false;

  async function confirmPending() {
    if (!pending) return;
    await onToggleAgent(pending.ref, pending.action === 'add');
    setPending(null);
  }

  // Linha de agente — wireframe .toolchip (allow = com acesso, check verde;
  // deny = sem acesso, plus). Badges de tipo/escopo viram sufixo discreto.
  function AgentRow({
    ref_,
    name,
    slug,
    archetype,
    scope,
    mode,
  }: {
    ref_: string;
    name: string;
    slug?: string | null;
    archetype?: string | null;
    scope?: string | null;
    mode: 'remove' | 'add';
  }) {
    const aMeta = archetypeMeta(archetype);
    const sMeta = scopeMeta(scope);
    const saving = savingAgent === ref_;
    return (
      <button
        type="button"
        disabled={saving}
        onClick={() => setPending({ ref: ref_, name, action: mode })}
        title={mode === 'remove' ? 'Clique pra remover o acesso' : 'Clique pra dar acesso'}
        className={cn('toolchip', mode === 'remove' ? 'allow' : 'deny')}
      >
        {saving ? (
          <Loader2 className="ic animate-spin" aria-hidden />
        ) : mode === 'remove' ? (
          <Check className="ic" aria-hidden />
        ) : (
          <Plus className="ic" aria-hidden />
        )}
        <span className="truncate">{name}</span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: '.6rem',
            color: 'var(--wf-muted)',
            whiteSpace: 'nowrap',
          }}
        >
          {aMeta.label}
          {scope ? ` · ${sMeta.label}` : ''}
          {slug ? ` · ${slug}` : ''}
        </span>
      </button>
    );
  }

  return (
    <>
      <Dialog open={!!tool} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[88vh] p-0 overflow-hidden flex flex-col gap-0">
          {tool && (
            <>
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-raios-border/50 space-y-0 text-left">
                <DialogTitle className="flex items-center gap-2 min-w-0 pr-8">
                  Agentes com acesso{' '}
                  <code className="truncate text-base text-raios-accent-text">{tool.slug}</code>
                </DialogTitle>
                <DialogDescription className="mt-1">
                  Clique num agente <strong>com acesso</strong> para remover, ou num{' '}
                  <strong>sem acesso</strong> para liberar — abre uma confirmação.
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 min-w-0">
                {tool.is_core && (
                  <div className="rounded-lg border border-raios-accent/30 bg-raios-accent/10 p-3 text-xs leading-relaxed text-raios-text-secondary flex items-start gap-2.5">
                    <Sparkles className="size-4 text-raios-accent shrink-0 mt-0.5" />
                    <span>
                      Tool marcada como <strong>Core</strong> — está automaticamente em{' '}
                      <strong>
                        {tool.is_companion_only ? 'todos os companions' : 'todos os agentes'}
                      </strong>{' '}
                      (atuais e futuros). Liberar manualmente não é necessário; remoções podem ser
                      reconciliadas de volta pelo Core.
                    </span>
                  </div>
                )}

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-raios-muted pointer-events-none" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar agente por nome ou slug…"
                    className="w-full rounded-lg border border-raios-border bg-raios-card pl-9 pr-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40"
                  />
                </div>

                {/* Duas colunas — wireframe .twocol-tools/.toolcol */}
                <div className="twocol-tools min-w-0">
                  {/* COM ACESSO */}
                  <div className="toolcol min-w-0">
                    <h5>
                      <Check className="ic" style={{ color: 'var(--wf-success-text)' }} aria-hidden /> Com acesso{' '}
                      <span style={{ textTransform: 'none' }}>
                        {loadingAgents ? '' : `(${withAccess.length})`}
                      </span>
                    </h5>
                    {loadingAgents ? (
                      <div className="space-y-2">
                        {Array.from({ length: 4 }).map((_, i) => (
                          <div
                            key={i}
                            className="h-12 rounded-lg border border-raios-border bg-raios-elevated animate-pulse"
                          />
                        ))}
                      </div>
                    ) : withAccess.length === 0 ? (
                      <p className="text-xs text-raios-muted py-2">
                        {q
                          ? 'Nenhum agente com acesso bate com a busca.'
                          : tool.is_core
                            ? 'Nenhum agente ainda — aguarde a aplicação do Core.'
                            : 'Nenhum agente com acesso. Clique num agente ao lado pra liberar.'}
                      </p>
                    ) : (
                      <>
                        <div>
                          {pageItems.map((at) => {
                            const ref = at.agent_slug ?? at.agent_id;
                            const ag = at.agent_slug ? agentBySlug.get(at.agent_slug) : undefined;
                            return (
                              <AgentRow
                                key={`${at.agent_id}-${at.tool_slug}`}
                                ref_={ref}
                                name={at.agent_name ?? at.agent_slug ?? at.agent_id}
                                slug={at.agent_slug}
                                archetype={ag?.archetype ?? at.agent_archetype}
                                scope={ag?.scope ?? null}
                                mode="remove"
                              />
                            );
                          })}
                        </div>
                        {withAccess.length > AGENTS_PER_PAGE && (
                          <div className="flex items-center justify-between gap-2 mt-2">
                            <span className="text-xs text-raios-muted">
                              Página {page + 1} de {pageCount}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                disabled={page === 0}
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                aria-label="Página anterior"
                                className="inline-flex items-center gap-1 rounded-md border border-raios-border px-2 py-1 text-xs font-medium hover:border-raios-accent hover:text-raios-accent transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <ChevronLeft className="size-3.5" />
                              </button>
                              <button
                                type="button"
                                disabled={page + 1 >= pageCount}
                                onClick={() => setPage((p) => p + 1)}
                                aria-label="Próxima página"
                                className="inline-flex items-center gap-1 rounded-md border border-raios-border px-2 py-1 text-xs font-medium hover:border-raios-accent hover:text-raios-accent transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                <ChevronRight className="size-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* SEM ACESSO */}
                  <div className="toolcol min-w-0">
                    <h5>
                      <X className="ic" aria-hidden /> Sem acesso{' '}
                      <span style={{ textTransform: 'none' }}>({withoutAccess.length})</span>
                    </h5>
                    {withoutAccess.length === 0 ? (
                      <p className="text-xs text-raios-muted py-2">
                        {q ? 'Nenhum agente sem acesso bate com a busca.' : 'Todos os agentes já têm esta tool.'}
                      </p>
                    ) : (
                      <div className="max-h-96 overflow-y-auto pr-1">
                        {withoutAccess.slice(0, 100).map((a) => (
                          <AgentRow
                            key={a.slug}
                            ref_={a.slug}
                            name={a.name ?? a.slug}
                            slug={a.slug}
                            archetype={a.archetype}
                            scope={a.scope}
                            mode="add"
                          />
                        ))}
                        {withoutAccess.length > 100 && (
                          <p className="p-2.5 text-xs text-raios-muted">
                            +{withoutAccess.length - 100} agentes — refine a busca.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <DialogFooter className="px-6 py-4 border-t border-raios-border/50">
                <Button variant="ghost" onClick={onClose}>
                  Fechar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmação — dlgTAConfirm do wireframe */}
      <Dialog open={!!pending} onOpenChange={(open) => !open && !confirming && setPending(null)}>
        <DialogContent className="max-w-sm">
          {pending && (
            <>
              <DialogHeader>
                <DialogTitle>Confirmar — {pending.name}</DialogTitle>
                <DialogDescription>
                  {pending.action === 'add' ? (
                    <>
                      Dar a <strong>{pending.name}</strong> acesso a esta tool?
                    </>
                  ) : (
                    <>
                      Remover o acesso de <strong>{pending.name}</strong> a esta tool?
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" disabled={confirming} onClick={() => setPending(null)}>
                  Cancelar
                </Button>
                <Button
                  variant={pending.action === 'add' ? 'brand' : 'destructive'}
                  disabled={confirming}
                  onClick={() => void confirmPending()}
                >
                  {confirming && <Loader2 className="size-4 animate-spin" />}
                  {pending.action === 'add' ? 'Dar acesso' : 'Remover'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
